import type { ConceptChipApi } from '@mathflat/domain/@entities/ConceptChip/api'
import { computed, makeObservable, observable, reaction, runInAction } from 'mobx'
import type { COLOR, DIVIDE } from 'src/constant'
import { EMPTY_NUMBER_ID, MAX_REQUEST_NUMBER } from 'src/constant/common'
import type { WorksheetDesignTemplateT } from 'src/contentComponents/common/WorksheetDesign/DesignTemplate.service'
import type { MakeWorksheetPreviewOptionFilter } from 'src/dto'
import { ReqParams } from 'src/dto'

import dateUtils from '@/utils/DateUtils'

import { DTO } from '../DTO'

export class WorksheetDesignOption extends DTO {
  id = EMPTY_NUMBER_ID
  name = '' // 학습지 디자인 제목
  layoutType: WorksheetDesignTemplateT['id'] = 0 // 학습지 레이아웃 코드
  layoutColor: (typeof COLOR)[number]['id'] = 'BLUE' // 학습지 색상 enum
  partitionType: (typeof DIVIDE)[number]['id'] = 0 // 분할타입
  wrongAnswerNoteFlag = false // 오답노트 렌더여부
  conceptNameFlag = false // 유형명 렌더여부
  answerRateFlag = false // 정답률 렌더여부
  relationWorkbookFlag = false // 연관교재 정보 렌더 여부
  includeProblemFlag = false // 정답지 렌더 여부
  mode: 'create' | 'modify' | 'default' = 'default' // 학습지 설정에서 생성인지 수정인지 분기값
  reqParams = new ReqParams({
    params: { size: MAX_REQUEST_NUMBER, sorts: [['createDatetime', 'DESC']] },
  })
  pdfDate: Date | null = new Date()
  pdfDateType: common.PdfDateType = 'TODAY'
  designTemplateId = this.id // 설정된 디자인 템플릿 id
  qrFlag = true // qr코드 렌더 여부
  qrAvailable = true // q코드 사용 가능 디자인 여부
  problemTrendFlag = false // 신경향 라벨 렌더 여부

  constructor(data?: Partial<WorksheetDesignOption>) {
    super()
    if (data) {
      Object.assign(this, data)
    }

    makeObservable(this, {
      id: observable,
      name: observable,
      layoutType: observable,
      layoutColor: observable,
      wrongAnswerNoteFlag: observable,
      conceptNameFlag: observable,
      answerRateFlag: observable,
      partitionType: observable,
      mode: observable,
      reqParams: observable,
      relationWorkbookFlag: observable,
      includeProblemFlag: observable,
      pdfDate: observable,
      pdfDateType: observable,
      designTemplateId: observable,
      qrFlag: observable,
      qrAvailable: observable,
      problemTrendFlag: observable,

      params: computed,
      conceptChipPdfParams: computed,
      createDisabled: computed,
    })

    reaction(
      () => this.wrongAnswerNoteFlag,
      () => {
        if (this.wrongAnswerNoteFlag) {
          runInAction(() => {
            this.partitionType = 0
          })
        }
      },
    )

    reaction(
      () => this.partitionType,
      () => {
        if (this.wrongAnswerNoteFlag) {
          runInAction(() => {
            this.partitionType = 0
          })
        }
      },
    )
  }

  // TODO: 변수명을 BE 최신 가이드라인에 맞게 변경하고 리팩토링 필요
  get params(): req.WorksheetDesignDetail &
    Pick<MakeWorksheetPreviewOptionFilter, 'designTemplateId' | 'pdfDateType'> &
    Pick<req.CreateWorksheet, 'pdfDate'> {
    return {
      designTemplateId: this.id,
      name: this.name,
      layoutType: this.layoutType,
      layoutColor: this.layoutColor,
      wrongAnswerNoteFlag: this.wrongAnswerNoteFlag,
      conceptNameFlag: this.conceptNameFlag,
      answerRateFlag: this.answerRateFlag,
      partitionType: this.partitionType,
      relationWorkbookFlag: this.relationWorkbookFlag,
      includeProblemFlag: this.includeProblemFlag,
      pdfDate: dateUtils.toDateString(this.pdfDate ?? new Date(), '-'),
      pdfDateType: this.pdfDateType,
      qrFlag: this.qrFlag,
      problemTrendFlag: this.problemTrendFlag,
    }
  }

  // params와 동일한 값이나, 변수명을 BE 최신 가이드라인에 맞게 변경
  get conceptChipPdfParams(): Pick<
    ConceptChipApi.Request.PostGroupPersonalizedWeakConceptChipParams,
    | 'designTemplateId'
    | 'layoutType'
    | 'layoutColor'
    | 'partitionType'
    | 'includeQr'
    | 'includeConceptName'
    | 'includeCorrectAnswerRate'
    | 'includeTrendLabel'
    | 'includeWrongAnswerNote'
    | 'includeProblemInAnswerSolutionPage'
    | 'pdfDateType'
    | 'pdfDate'
  > {
    return {
      // EMPTY_NUMBER_ID(-1)로 보내면 터지는데 왜 nullable로 관리 안했을까요.
      // 반드시 고쳐야 합니다.
      designTemplateId: this.id === EMPTY_NUMBER_ID ? null : this.id,
      layoutType: this.layoutType,
      layoutColor: this.layoutColor,
      partitionType: this.partitionType,
      includeQr: this.qrFlag,
      includeConceptName: this.conceptNameFlag,
      includeCorrectAnswerRate: this.answerRateFlag,
      includeTrendLabel: this.problemTrendFlag,
      includeWrongAnswerNote: this.wrongAnswerNoteFlag,
      includeProblemInAnswerSolutionPage: this.includeProblemFlag,
      pdfDateType: this.pdfDateType,
      pdfDate: this.pdfDate ? dateUtils.toDateString(this.pdfDate, '-') : null,
    }
  }

  // TODO: designTemplateId은 로컬 urlOptions에서만 쓰므로 params대신 이걸 써야하지만.. 너무 리팩토링 범위가 넓어서 일단 todo
  // get urlOptions(): req.WorksheetDesignDetail &
  //   Pick<MakeWorksheetPreviewOptionFilter, 'designTemplateId'> {
  //   return {
  //     designTemplateId: this.id,
  //     ...this.params,
  //   }
  // }

  get createDisabled() {
    return this.name.length < 1 || this.name.length > 30
  }
}
